import { useMemo, useState, type FormEvent } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Button } from '@shared/ui/Button';
import { Modal } from '@shared/ui/Modal';
import { ConfirmDialog } from '@shared/ui/ConfirmDialog';
import { Field, Input, Label } from '@shared/ui/Input';
import { Badge } from '@shared/ui/Badge';
import {
  useListMailboxRoutesQuery,
  useListMailboxCategoriesQuery,
  useCreateMailboxCategoryMutation,
  useUpdateMailboxCategoryMutation,
  useDeleteMailboxCategoryMutation,
  useSetMailboxCategoryRoutesMutation,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import type { MailboxCategory, MailboxCategoryInput } from '@entities/legal/types';

const Toolbar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const Intro = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  max-width: 720px;
  font-size: 13px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const List = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing(4)};
`;

const CategoryCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const CardHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const HeadMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  min-width: 0;
`;

const CatName = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  flex-wrap: wrap;
`;

const Code = styled.code`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Desc = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const HeadActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  flex-shrink: 0;
  flex-wrap: wrap;
`;

const RoutesBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2)};
  padding-top: ${({ theme }) => theme.spacing(3)};
  border-top: 1px dashed ${({ theme }) => theme.colors.border};
`;

const RoutesLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const CheckGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: ${({ theme }) => theme.spacing(2)};
`;

const CheckRow = styled.label`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  transition: border-color ${({ theme }) => theme.motion.fast}
    ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
  }
  &:focus-within {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 1px;
  }

  input {
    margin-top: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const CheckText = styled.span`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const RouteEmail = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const RoutesFooter = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const Muted = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing(8)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const ErrorBox = styled.div`
  color: ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => `${theme.colors.status.danger}15`};
  border: 1px solid ${({ theme }) => `${theme.colors.status.danger}33`};
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing(3)};
  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const FullRow = styled.div`
  grid-column: 1 / -1;
`;

const DescriptionInput = styled.textarea`
  width: 100%;
  min-height: 140px;
  padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font: inherit;
  font-size: 14px;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  resize: vertical;
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -1px;
  }
`;

const Hint = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: flex-end;
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const CODE_RE = /^[A-Za-z0-9_-]+$/;

const emptyInput = (): MailboxCategoryInput => ({
  code: '',
  display_name: '',
  description: '',
  is_active: true,
});

interface RouteCheckboxesProps {
  category: MailboxCategory;
  canWrite: boolean;
}

/** Маппинг category → ящики. Локальное состояние выбора, сохраняется одним
 *  PUT (setRoutes — backend перезаписывает связь целиком). */
function RouteCheckboxes({ category, canWrite }: RouteCheckboxesProps) {
  const { data: routesData } = useListMailboxRoutesQuery();
  const [setRoutes, setRoutesState] = useSetMailboxCategoryRoutesMutation();
  const [selected, setSelected] = useState<string[]>(category.route_ids);
  const [error, setError] = useState<string | null>(null);

  const routes = useMemo(() => routesData?.items?.filter((r) => r.is_active) ?? [], [routesData]);

  // Грязно, если набор отличается от сохранённого на сервере.
  const dirty = useMemo(() => {
    const a = [...selected].sort();
    const b = [...category.route_ids].sort();
    return a.length !== b.length || a.some((v, i) => v !== b[i]);
  }, [selected, category.route_ids]);

  const toggle = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const save = async () => {
    setError(null);
    try {
      await setRoutes({ id: category.id, route_ids: selected }).unwrap();
    } catch (err: unknown) {
      setError(
        (err as { data?: { detail?: string } })?.data?.detail ??
          (err as { message?: string })?.message ??
          'Не удалось сохранить маппинг ящиков.',
      );
    }
  };

  if (routes.length === 0) {
    return (
      <RoutesBlock>
        <RoutesLabel>Ящики этой категории</RoutesLabel>
        <Muted>
          Активных ящиков отделов пока нет — добавьте их во вкладке «Маршруты отделов», затем
          привяжите к категории.
        </Muted>
      </RoutesBlock>
    );
  }

  return (
    <RoutesBlock>
      <RoutesLabel>
        Ящики этой категории{selected.length > 0 ? ` (${selected.length})` : ''}
      </RoutesLabel>
      {error && <ErrorBox>{error}</ErrorBox>}
      <CheckGrid>
        {routes.map((r) => (
          <CheckRow key={r.id}>
            <input
              type="checkbox"
              checked={selected.includes(r.id)}
              onChange={() => toggle(r.id)}
              disabled={!canWrite}
            />
            <CheckText>
              <span>{r.display_name}</span>
              <RouteEmail>{r.email}</RouteEmail>
            </CheckText>
          </CheckRow>
        ))}
      </CheckGrid>
      {canWrite && (
        <RoutesFooter>
          <Button
            $size="sm"
            $variant="primary"
            onClick={save}
            disabled={!dirty || setRoutesState.isLoading}
          >
            {setRoutesState.isLoading ? 'Сохраняю…' : 'Сохранить ящики'}
          </Button>
          {dirty && <Muted>Есть несохранённые изменения</Muted>}
        </RoutesFooter>
      )}
    </RoutesBlock>
  );
}

export function MailboxCategoriesTab() {
  // Настройка категорий доступна тем, у кого есть право записи на агента
  // диспетчера (admin сквозной + dispatcher_operator). Симметрично вкладке
  // «Маршруты отделов» и бэкенд-гарду require_agent_access('dispatcher', write).
  const { can_write: canWrite } = useAgentPermission('dispatcher');

  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading, error } = useListMailboxCategoriesQuery({
    include_inactive: includeInactive,
  });
  const [createMutation, createState] = useCreateMailboxCategoryMutation();
  const [updateMutation, updateState] = useUpdateMailboxCategoryMutation();
  const [deleteMutation] = useDeleteMailboxCategoryMutation();

  const [editing, setEditing] = useState<MailboxCategory | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<MailboxCategoryInput>(emptyInput());
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const items = data?.items ?? [];

  const openCreate = () => {
    setForm(emptyInput());
    setFormError(null);
    setCreating(true);
  };
  const openEdit = (c: MailboxCategory) => {
    setEditing(c);
    setForm({
      code: c.code,
      display_name: c.display_name,
      description: c.description,
      is_active: c.is_active,
    });
    setFormError(null);
  };
  const closeModal = () => {
    setEditing(null);
    setCreating(false);
    setFormError(null);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    const code = form.code.trim();
    if (!CODE_RE.test(code)) {
      setFormError('Код может содержать только латиницу, цифры, «_» и «-».');
      return;
    }
    const payload: MailboxCategoryInput = {
      code,
      display_name: form.display_name.trim(),
      description: form.description.trim(),
      is_active: form.is_active ?? true,
    };
    try {
      if (editing) {
        await updateMutation({ id: editing.id, input: payload }).unwrap();
      } else {
        await createMutation(payload).unwrap();
      }
      closeModal();
    } catch (err: unknown) {
      setFormError(
        (err as { data?: { detail?: string } })?.data?.detail ??
          (err as { message?: string })?.message ??
          'Не удалось сохранить категорию. Проверьте поля.',
      );
    }
  };

  return (
    <div>
      <Intro>
        Категория — это смысловой класс письма (заявка на снабжение, тендер, претензия и т.п.).
        Агент-диспетчер классифицирует письмо в одну категорию, а затем разворачивает её в набор
        ящиков отделов по маппингу ниже. Описание категории попадает в промпт классификатора — чем
        точнее описание, тем точнее разбор.
      </Intro>

      <Toolbar>
        <Label as="label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          Показывать отключённые
        </Label>
        {canWrite && (
          <Button $variant="primary" onClick={openCreate}>
            + Новая категория
          </Button>
        )}
      </Toolbar>

      {error && <ErrorBox>Не удалось загрузить категории.</ErrorBox>}

      {isLoading ? (
        <EmptyState>Загрузка…</EmptyState>
      ) : items.length === 0 ? (
        <EmptyState>
          Категории ещё не настроены. {canWrite ? 'Создайте первую — ' : ''}
          на их основе диспетчер раскладывает письма по ящикам отделов.
        </EmptyState>
      ) : (
        <List>
          {items.map((c) => (
            <CategoryCard key={c.id}>
              <CardHead>
                <HeadMain>
                  <CatName>
                    {c.display_name}
                    <Code>{c.code}</Code>
                    {c.is_active ? (
                      <Badge $tone="success">активна</Badge>
                    ) : (
                      <Badge $tone="neutral">отключена</Badge>
                    )}
                  </CatName>
                  <Desc>{c.description}</Desc>
                </HeadMain>
                {canWrite && (
                  <HeadActions>
                    <Button $size="sm" $variant="ghost" onClick={() => openEdit(c)}>
                      Редактировать
                    </Button>
                    {c.is_active && (
                      <Button $size="sm" $variant="danger" onClick={() => setConfirmDeleteId(c.id)}>
                        Отключить
                      </Button>
                    )}
                  </HeadActions>
                )}
              </CardHead>
              <RouteCheckboxes category={c} canWrite={canWrite} />
            </CategoryCard>
          ))}
        </List>
      )}

      <Modal
        open={creating || editing !== null}
        onClose={closeModal}
        title={editing ? `Категория ${editing.display_name}` : 'Новая категория'}
      >
        <form onSubmit={submit}>
          {formError && <ErrorBox>{formError}</ErrorBox>}
          <FormGrid>
            <Field>
              <Label>Код</Label>
              <Input
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
                placeholder="material_supply_request"
                required
              />
              <Hint>Латиница, цифры, «_», «-». Неизменяемый якорь маппинга.</Hint>
            </Field>
            <Field>
              <Label>Название</Label>
              <Input
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                placeholder="Заявка на снабжение / материалы"
                required
              />
            </Field>
            <Field>
              <Label as="label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={form.is_active ?? true}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Активна
              </Label>
            </Field>
            <FullRow>
              <Field>
                <Label>Описание (учится классификатор)</Label>
                <DescriptionInput
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Какие письма относятся к этой категории: тематика, типы вложений, типичные действия. Пример: «Заявки на поставку строительных материалов и МТО (песок, щебень, бетон, арматура). Требуют подтверждения потребности участком, расчёта стоимости тендерным отделом, заключения договора юр.отделом.»"
                  required
                  minLength={20}
                />
                <Hint>
                  Это описание попадает в промпт LLM при классификации. Минимум 20 символов.
                </Hint>
              </Field>
            </FullRow>
          </FormGrid>
          <ModalActions>
            <Button type="button" $variant="ghost" onClick={closeModal}>
              Отмена
            </Button>
            <Button
              type="submit"
              $variant="primary"
              disabled={createState.isLoading || updateState.isLoading}
            >
              {editing ? 'Сохранить изменения' : 'Создать категорию'}
            </Button>
          </ModalActions>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDeleteId !== null}
        title="Отключить категорию?"
        warning="Письма перестанут классифицироваться в эту категорию. Маппинг на ящики сохранится — категорию можно вернуть в работу включением «Активна»."
        confirmText={items.find((c) => c.id === confirmDeleteId)?.code ?? ''}
        confirmLabel="Введите код категории для подтверждения"
        confirmButtonLabel="Отключить"
        busyLabel="Отключение…"
        onCancel={() => setConfirmDeleteId(null)}
        onConfirm={async () => {
          if (confirmDeleteId) {
            await deleteMutation(confirmDeleteId)
              .unwrap()
              .catch(() => {});
          }
          setConfirmDeleteId(null);
        }}
      />
    </div>
  );
}
